import SlimSelect from 'slim-select';

/**
 * Controls select options.
 */
export default class ContactFormController {
  private readonly companySelect: SlimSelect | null;
  private readonly productFamilySelect: SlimSelect | null;
  private readonly countrySelect: SlimSelect | null;
  private readonly stateSelect: SlimSelect | null;
  private readonly companySelectElement: string = '.js-slimSelect-contactForm.companies';
  private readonly countrySelectElement: string = '.js-slimSelect-contactForm.countries';
  private readonly stateSelectElement: string = '.js-slimSelect-contactForm.states';
  private readonly productFamiliesSelectElement: string = '.js-slimSelect-contactForm.productFamilies';

  // All existing select #2 options.
  private readonly productFamilyOptions = document.querySelector(this.productFamiliesSelectElement)?.querySelectorAll('select > option');

  // "nothing selected" string, needed for correct translation.
  private readonly selectValueString = this.productFamilyOptions?.item(0).textContent;

  // First select option dummy.
  private readonly selectValueOption: { value: string, text: string, placeholder: boolean } = {
    value: this.selectValueString ? this.selectValueString : '',
    text: this.selectValueString ? this.selectValueString : '',
    placeholder: true,
  };

  // Options array, valid for SlimSelect::setData().
  private productFamilyOptionsOriginal: { value: string, text: string, placeholder?: boolean }[] = [this.selectValueOption];

  constructor() {

    const whiteSpaceValidationElements = document.querySelectorAll('.js-validateWhitespace');
    whiteSpaceValidationElements.forEach((elem) => {
      if (elem instanceof HTMLInputElement || elem instanceof HTMLTextAreaElement) {
        elem.addEventListener('input', () => {
          this.validateWhiteSpace(elem);
        });
      }
    });

    const contactFormCountry: HTMLSelectElement | null = document.querySelector('.js-countrySelect');

    if (contactFormCountry) {
      this.contactFormCountrySelect();
      contactFormCountry.addEventListener('change', () => {
        this.contactFormCountrySelect();
      });
    }

    // Initiate both select boxes.
    this.companySelect = new SlimSelect({
      select: this.companySelectElement,
      showSearch: false,
    });
    this.countrySelect = new SlimSelect({
      select: this.countrySelectElement,
      showSearch: true,
    });
    this.stateSelect = new SlimSelect({
      select: this.stateSelectElement,
      showSearch: true,
    });
    this.productFamilySelect = new SlimSelect({
      select: this.productFamiliesSelectElement,
      showSearch: false,
    });

    // Prepare option changing array.
    let productFamilyOptionsChanged: { value: string, text: string, placeholder?: boolean }[] = [this.selectValueOption];

    // On some pages, the productFamily has to be preselected. Therefore first fill up options, then select the desired option.
    if (this.productFamilyOptions?.length && this.companySelect?.selected() && this.productFamilySelect?.selected()) {
      this.createAndSetSelectOptions(this.companySelect?.selected(), productFamilyOptionsChanged, this.productFamilySelect?.selected() as string);
    }

    // Fill up options Array with all existing options.
    this.productFamilyOptions?.forEach((option) => {
      this.productFamilyOptionsOriginal.push({
        value: (option as HTMLInputElement)?.value,
        text: option.textContent ? option.textContent : '',
      });
    });

    // Listen on select #1 changes, modify select #2 options.
    document.querySelector(this.companySelectElement)?.addEventListener('change', () => {
      const selectedCompany = this.companySelect?.selected();
      const selectedProductFamily = this.productFamilySelect?.selected();

      // Empty changed options.
      productFamilyOptionsChanged = [this.selectValueOption];

      // Change to "nothing selected": replace select #2 options with original options (like resetting).
      // No further process.
      if (!selectedCompany) {
        this.productFamilySelect?.setData(this.productFamilyOptionsOriginal);
        return;
      }

      // Change to another company, while:
      // - select #2 has selection
      // - select #2 selection is not "nothing selected"
      // - select #2 selection's company is not the current selected #1 company.
      // => replace select #2 options with original options (like resetting).
      // further process: limit the original options by new company selection.
      if (
        selectedCompany &&
        selectedProductFamily &&
        selectedProductFamily.indexOf(this.selectValueString as string) === -1
        && selectedProductFamily.indexOf(selectedCompany as string) === -1) {
        this.productFamilySelect?.setData(this.productFamilyOptionsOriginal);
      }

      // Limit select #2 options depending on select #1 selection.
      if (this.productFamilyOptions?.length) {
        this.createAndSetSelectOptions(selectedCompany, productFamilyOptionsChanged);
      }
    });
  }

  /**
   * Creates dynamic option set and, if needed, preselects an option.
   *
   * @param selectedCompany
   * @param productFamilyOptionsChanged
   * @param preselectedProductFamily
   */
  createAndSetSelectOptions(selectedCompany, productFamilyOptionsChanged, preselectedProductFamily = '') {

    this.productFamilyOptions?.forEach((option, key) => {
      let optionCompany = option.getAttribute('data-company');

      // Only productFamily with currently selected company passes.
      let optionValue = (option as HTMLInputElement)?.value;
      if (optionCompany && selectedCompany && optionCompany === selectedCompany) {
        productFamilyOptionsChanged.push({
          value: optionValue,
          text: option.textContent ? option.textContent : '',
        });
      }
    });

    // Assign changed options to select #2, preselect option if needed.
    this.productFamilySelect?.setData(productFamilyOptionsChanged);
    if (preselectedProductFamily && this.productFamilySelect?.selected()) {
      this.productFamilySelect?.set(preselectedProductFamily);
    }
  }

  contactFormCountrySelect() {
    const contactFormCountry: HTMLSelectElement | null = document.querySelector('.js-countrySelect');
    const contactFormState: HTMLSelectElement | null = document.querySelector('.js-stateSelect');
    const contactFormStateWrapper: HTMLElement | null = document.querySelector('.js-stateSelectWrapper');
    if (contactFormCountry) {
      if (contactFormCountry.value === '220') {
        contactFormState?.classList.remove('hide');
        contactFormStateWrapper?.classList.remove('hide');
      } else {
        contactFormState?.classList.add('hide');
        contactFormStateWrapper?.classList.add('hide');
      }
    }
  }

  validateWhiteSpace(elem: HTMLInputElement | HTMLTextAreaElement) {
    const val = elem.value;
    if (val.trim() === '') {
      elem.value = '';
    }
  }
}
